"use client";

import React from "react";

import { EngineVisualCard } from "./_EngineVisualCard";
import { localePicker } from "./_locale-text";

export type SadeSatiPhase = "rising" | "peak" | "setting" | "dhaiya_4th" | "dhaiya_8th" | "none";

export interface SadeSatiData {
  isActive: boolean;
  phase: SadeSatiPhase;
  phaseTitleEn: string;
  phaseTitleHi: string;
  startYear?: string;
  endYear?: string;
  currentSaturnSignEn?: string;
  currentSaturnSignHi?: string;
  moonSignEn: string;
  moonSignHi: string;
  impactSummaryEn: string;
  impactSummaryHi: string;
  remediesEn: string[];
  remediesHi: string[];
}

export interface SadeSatiPhaseMeterProps {
  data: SadeSatiData;
  locale?: "en" | "hi";
}

type Pick = <T,>(hi: T, en: T) => T;

/** Moon sign plus the live Saturn transit, or an honest gap.
 *
 * The transit sign previously fell back to "Pisces"/"मीन" when the engine had
 * not supplied one — a plausible-looking planetary position that no calculation
 * produced. Rule 29: an unknown value is named as unknown, never substituted.
 */
function transitLine(data: SadeSatiData, isHi: boolean): string {
  const moon = isHi ? data.moonSignHi : data.moonSignEn;
  const saturn = isHi ? data.currentSaturnSignHi : data.currentSaturnSignEn;

  if (!saturn) {
    return isHi ? `चंद्र राशि: ${moon}` : `Moon Sign: ${moon}`;
  }
  return isHi
    ? `चंद्र राशि: ${moon} | वर्तमान शनि गोचर: ${saturn}`
    : `Moon Sign: ${moon} | Current Saturn Transit: ${saturn}`;
}

function PhaseBadge({
  data,
  isHi,
  pick,
}: {
  data: SadeSatiData;
  isHi: boolean;
  pick: Pick;
}) {
  const label = data.isActive
    ? isHi
      ? data.phaseTitleHi
      : data.phaseTitleEn
    : pick("साढ़े साती मुक्त (शुभ)", "No Active Sade Sati");

  return (
    <div
      className={`inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-xs font-semibold sm:self-auto ${
        data.isActive
          ? "border border-warning/40 bg-warning/15 text-warning"
          : "border border-success/40 bg-success/15 text-success"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${data.isActive ? "animate-pulse bg-warning" : "bg-success"}`}
      />
      {label}
    </div>
  );
}

export default function SadeSatiPhaseMeter({
  data,
  locale = "en",
}: SadeSatiPhaseMeterProps) {
  const isHi = locale === "hi";
  const pick = localePicker(isHi);

  const getPhaseStep = (phase: SadeSatiPhase): number => {
    switch (phase) {
      case "rising":
        return 1;
      case "peak":
        return 2;
      case "setting":
        return 3;
      case "dhaiya_4th":
      case "dhaiya_8th":
        return 2; // Dhaiya special alert
      default:
        return 0;
    }
  };

  const currentStep = getPhaseStep(data.phase);
  const isDhaiya = data.phase === "dhaiya_4th" || data.phase === "dhaiya_8th";

  return (
    <EngineVisualCard
      ariaLabel={pick("शनि साढ़े साती एवं ढैय्या चक्र", "Shani Sade Sati & Dhaiya Phase Meter")}
      title={
        <span className="flex items-center gap-2">
          <span className="text-xl">🪐</span>
          {pick("शनि साढ़े साती एवं ढैय्या स्थिति", "Shani Sade Sati & Dhaiya Status")}
        </span>
      }
      subtitle={transitLine(data, isHi)}
      badge={<PhaseBadge data={data} isHi={isHi} pick={pick} />}
    >
      {/* 3-Phase Visual Progress Meter (Only if Sade Sati or general flow) */}
      {!isDhaiya && (
        <div className="my-6">
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            {/* Phase 1 */}
            <div
              className={`p-3 rounded-xl border transition-all ${
                currentStep === 1
                  ? "bg-warning/10 border-warning ring-2 ring-warning/60 font-bold"
                  : currentStep > 1
                  ? "bg-surface-canvas border-border-subtle text-text-muted"
                  : "bg-surface-raised border-border-subtle text-text-secondary"
              }`}
            >
              <div className="text-eyebrow uppercase tracking-eyebrow text-text-muted mb-1">
                {pick("प्रथम चरण", "Phase 1 (Rising)")}
              </div>
              <div className="font-semibold text-text-primary">
                {pick("उदय काल (व्यय भाव)", "12th House Transit")}
              </div>
              <div className="text-caption text-text-muted mt-1">
                {pick("मानसिक तनाव व यात्राएं", "Mental focus & expenses")}
              </div>
            </div>

            {/* Phase 2 */}
            <div
              className={`p-3 rounded-xl border transition-all ${
                currentStep === 2
                  ? "bg-error/10 border-error ring-2 ring-error/60 font-bold"
                  : currentStep > 2
                  ? "bg-surface-canvas border-border-subtle text-text-muted"
                  : "bg-surface-raised border-border-subtle text-text-secondary"
              }`}
            >
              <div className="text-eyebrow uppercase tracking-eyebrow text-text-muted mb-1">
                {pick("द्वितीय चरण (शिखर)", "Phase 2 (Peak)")}
              </div>
              <div className="font-semibold text-text-primary">
                {pick("जन्म चंद्र पर गोचर", "Over Natal Moon")}
              </div>
              <div className="text-caption text-text-muted mt-1">
                {pick("परिवर्तन व धैर्य परीक्षा", "Discipline & major shift")}
              </div>
            </div>

            {/* Phase 3 */}
            <div
              className={`p-3 rounded-xl border transition-all ${
                currentStep === 3
                  ? "bg-warning/10 border-warning ring-2 ring-warning/60 font-bold"
                  : "bg-surface-raised border-border-subtle text-text-secondary"
              }`}
            >
              <div className="text-eyebrow uppercase tracking-eyebrow text-text-muted mb-1">
                {pick("तृतीय चरण", "Phase 3 (Setting)")}
              </div>
              <div className="font-semibold text-text-primary">
                {pick("अस्त काल (धन भाव)", "2nd House Transit")}
              </div>
              <div className="text-caption text-text-muted mt-1">
                {pick("स्थिरता व राहत", "Recovery & stabilization")}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Impact Summary */}
      <div className="p-4 rounded-xl border mb-4 bg-surface-canvas border-border-subtle">
        <h4 className="text-xs sm:text-sm font-semibold text-text-primary mb-1">
          {pick("ज्योतिषीय विश्लेषण एवं प्रभाव:", "Astrological Analysis & Guidance:")}
        </h4>
        <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
          {isHi ? data.impactSummaryHi : data.impactSummaryEn}
        </p>
      </div>

      {/* Classical Upays / Remedies */}
      {data.remediesEn.length > 0 && (
        <div className="p-4 rounded-xl border bg-surface-brand-subtle border-border-interactive">
          <h4 className="text-xs sm:text-sm font-semibold text-text-primary mb-2 flex items-center gap-1.5">
            <span>🪔</span>
            {pick("प्रामाणिक वैदिक उपाय (Shani Upay):", "Recommended Vedic Upay (Remedies):")}
          </h4>
          <ul className="text-xs text-text-secondary space-y-1.5 list-disc list-inside">
            {(isHi ? data.remediesHi : data.remediesEn).map((remedy, idx) => (
              <li key={idx} className="leading-relaxed">
                {remedy}
              </li>
            ))}
          </ul>
        </div>
      )}
    </EngineVisualCard>
  );
}
